import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { enUS } from "date-fns/locale";
import { format as fmtFns } from "date-fns";
import TopBar from "@/components/TopBar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNum, fmtRiyal, toEnglishDigits } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime, combineDateTime, toLocalDateStr, localDateOffset, arabicDayOfWeek, computeDepartureRule } from "@/utils/date";
import { getAirlineNameOrCode, getBookingAirline, getBookingFlightNumber } from "@/utils/airline";
import { isTrainStation } from "@/utils/isTrainStation";
import { translateLocation } from "@/utils/locationI18n";
import { UpgradeCarModal } from "@/pages/BookingDetails";
import { computeSeats, buildCapacityMap, loadCachedCapacityMap, saveCachedCapacityMap } from "@/utils/seats";
import { urgencyLevel, urgencyStyle, timeUntil } from "@/utils/urgency";
import { buildWhatsAppLink, buildClientWhatsAppLink } from "@/utils/whatsapp";
import InlineCell from "@/components/InlineCell";
import { exportBookingsToExcel } from "@/utils/excel";
import DriverAssignModal from "@/components/DriverAssignModal";
import { buildDriverWhatsAppUrl } from "@/utils/whatsappDriver";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import CustomDatePicker from "@/components/DateTimePicker";
import { MagnifyingGlass, WhatsappLogo, ArrowLeft, ArrowUp, MicrosoftExcelLogo, UserPlus, PencilSimple, X, Phone, CalendarBlank, MagnifyingGlassMinus, MagnifyingGlassPlus, CaretUp, CaretDown, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";

// Extracts digits only (drops +, spaces, dashes) — for building wa.me links.
function phoneDigitsOnly(phone) {
  return String(phone || "").replace(/\D+/g, "");
}

// ── Same-client grouping helpers ─────────────────────────────────────────────
// Two bookings collapse into one merged row when they share the same
// (user_id, normalized client name, normalized phone). Trip date is NOT part
// of the key — a company that books the same passenger for 3 different days
// still gets ONE row.
function _normName(n) {
  return String(n || "").trim().replace(/\s+/g, " ").toLowerCase();
}
function _normPhone(p) {
  const first = Array.isArray(p) ? (p[0]?.number ?? p[0]) : p;
  return String(first || "")
    .replace(/[^\d]/g, "")   // digits only
    .replace(/^00/, "")       // 00966… → 966…
    .replace(/^0/, "");       // drop leading 0
}
function _groupKey(b) {
  const uid = b.user_id || `u:${_normName(b.user_name || b.company_name)}`;
  const cn = _normName(b.client_name || b.customer_name);
  const cp = _normPhone(b.client_phone || b.client_phones);
  // Grouping is scoped PER DAY (spec 2026-07): same client with multiple
  // bookings on the SAME `trip_date` share the seq + "حركة N/M" badge.
  // Different days are separate groups even for the same client.
  const day = (b.trip_date || (b.pickup_time || "").slice(0, 10) || "").slice(0, 10);
  return cn && cp && day ? `${day}|${uid}|${cn}|${cp}` : `solo:${b.id}`;
}
function _seqNumeric(b) {
  const s = String(b.seq_number || b.booking_number || "");
  const m = s.match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

// Column-header helper: renders label + a paired ▲▼ icon. Active arrow is gold,
// inactive is muted gray. Whole cell is clickable to toggle sort dir.
function SortHeader({ label, col, sortBy, sortDir, onToggle, className = "", nowrap = false }) {
  const isActive = sortBy === col;
  const asc = isActive && sortDir === "asc";
  const desc = isActive && sortDir === "desc";
  return (
    <th
      className={`p-3 text-start cursor-pointer select-none group hover:bg-primary/10 transition-colors ${nowrap ? "whitespace-nowrap" : ""} ${className}`}
      onClick={() => onToggle(col)}
      data-testid={`sort-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        <span>{label}</span>
        <span className="inline-flex flex-col leading-none">
          <CaretUp size={9} weight={asc ? "fill" : "regular"} className={asc ? "text-primary" : "text-white/25"} />
          <CaretDown size={9} weight={desc ? "fill" : "regular"} className={desc ? "text-primary" : "text-white/25"} />
        </span>
      </span>
    </th>
  );
}

// Renders one row per phone number with (number, primary badge, call, whatsapp).
function ClientPhonesCell({ booking, testIdBase, capacityMap }) {
  const phones = Array.isArray(booking.client_phones) && booking.client_phones.length > 0
    ? booking.client_phones
    : booking.client_phone ? [{ number: booking.client_phone, is_primary: true }] : [];
  if (phones.length === 0) return <span className="text-white/40">—</span>;
  return (
    <div className="space-y-1">
      {phones.map((p, i) => {
        const digits = phoneDigitsOnly(p.number);
        if (!digits) return null;
        return (
          <div key={i} className="inline-flex items-center gap-1.5" dir="ltr">
            <span className="text-white/85 num">{formatPhoneDisplay(p.number)}</span>
            {p.is_primary && phones.length > 1 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary font-bold">★</span>
            )}
            <a
              href={`tel:${p.number}`}
              onClick={(e) => e.stopPropagation()}
              title="اتصال"
              data-testid={`${testIdBase}-call-${i}`}
              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 hover:bg-primary/25 border border-primary/40 text-primary"
            >
              <Phone size={12} weight="fill" />
            </a>
            <a
              href={buildClientWhatsAppLink(booking, p.number, capacityMap)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="واتساب — تفاصيل الحجز"
              data-testid={`${testIdBase}-wa-${i}`}
              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] hover:bg-[#20BA5A]"
            >
              <WhatsappLogo size={12} weight="fill" className="text-white" />
            </a>
          </div>
        );
      })}
    </div>
  );
}

// Small circular WhatsApp icon button (24×24) rendered next to a phone number.
function PhoneWaButton({ phone, testId }) {
  const digits = phoneDigitsOnly(phone);
  if (!digits) return null;
  return (
    <a
      href={`https://wa.me/${digits}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="واتساب"
      data-testid={testId}
      className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] hover:bg-[#20BA5A] transition-all"
    >
      <WhatsappLogo size={14} weight="fill" className="text-white" />
    </a>
  );
}

// UX RULE (2026-07): booking status is ALWAYS "مؤكد" everywhere in the UI,
// regardless of what's stored in the DB. Cancel/reject/complete transitions
// are disabled — the display label + green style are hard-coded.
const CONFIRMED_STYLE = "text-[#2E7D32] border-[#2E7D32]/50 bg-[#2E7D32]/10";
const PENDING_STYLE = "text-[#F57F17] border-[#F57F17]/50 bg-[#F57F17]/10";
const REJECTED_STYLE = "text-destructive border-destructive/50 bg-destructive/10";

// Pick style + i18n label for a booking's status (2026-02 §StatusFlow).
function statusStyle(s) {
  if (s === "pending") return PENDING_STYLE;
  if (s === "rejected" || s === "cancelled") return REJECTED_STYLE;
  return CONFIRMED_STYLE; // confirmed / completed / anything else
}
function statusLabelKey(s) {
  if (s === "pending") return "bookingsPage.statusChips.pending";
  if (s === "rejected" || s === "cancelled") return "bookingsPage.statusChips.rejected";
  return "bookingsPage.statusChips.confirmed";
}

// Chip key lists — labels come from i18n at render time so switching language
// re-labels the chips instantly.
const DATE_CHIP_KEYS = ["past", "yesterday", "today", "tomorrow", "future"];
// 2026-02 §StatusFlow restored: admin filters by real status (قيد المراجعة /
// مؤكد / مرفوض) — the previous "always confirmed" collapse was removed.
const STATUS_CHIP_KEYS = ["all", "pending", "confirmed", "rejected"];

function todayISO() {
  return toLocalDateStr();
}
function shiftISO(days) {
  return localDateOffset(days);
}

export default function BookingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const isAdmin = !!(user?.is_admin || user?.role === "admin");
  // 2026-07 §CompanyBookingsFilter: read filter_company_id from URL when
  // navigated from /admin/companies (only admins reach this path).
  const [searchParams, setSearchParams] = useSearchParams();
  const filterCompanyId = isAdmin ? (searchParams.get("filter_company_id") || "") : "";
  const filterCompanyName = isAdmin ? (searchParams.get("filter_company_name") || "") : "";
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // Seed the capacity map from localStorage so the very first render already
  // knows every car's seat count — without waiting for the ~8MB /api/cars
  // response (base64 car images make it heavy on production).
  const [carsCapacity, setCarsCapacity] = useState(() => loadCachedCapacityMap());
  const [loading, setLoading] = useState(true);
  const [dateChip, setDateChip] = useState(""); // "" = all, else key from DATE_CHIPS
  const [specificDate, setSpecificDate] = useState(""); // YYYY-MM-DD (native <input type="date" lang="en">)
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const rangeActive = !!(rangeFrom || rangeTo);
  const [statusChip, setStatusChip] = useState("all");
  const [search, setSearch] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [upgradeFor, setUpgradeFor] = useState(null);
  // Sort + zoom UX controls (frontend-only).
  const [sortBy, setSortBy] = useState("trip_date");
  const [sortDir, setSortDir] = useState("asc");
  const [zoom, setZoom] = useState(1.0);
  // Slug → Arabic name map (e.g. `lexus` → `لكزس`) so per-car expanded rows
  // in the bookings table can render the ACTUAL per-car label, not inherit
  // the parent booking's `car_type_name` (which used to make every expanded
  // row show the header car for multi-car routes — root cause of the
  // "route with Ford + GMC only showed GMC" bug).
  const [carNameById, setCarNameById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, d, cars, carsFull] = await Promise.all([
        api.get("/bookings"),
        isAdmin ? api.get("/drivers") : Promise.resolve({ data: [] }),
        api.get("/cars/capacity").catch(() => api.get("/cars").catch(() => ({ data: [] }))),
        // Fetch full cars catalog too so we can build the slug → Arabic-name
        // map. /cars/capacity only returns {id, capacity} — no `name_ar` — so
        // the per-car row labels would fall back to raw slugs (`lexus`,
        // `ford`) without this second call.
        api.get("/cars").catch(() => ({ data: [] })),
      ]);
      setBookings(b.data);
      setDrivers(d.data);
      const nextMap = buildCapacityMap(cars.data);
      if (Object.keys(nextMap).length > 0) {
        setCarsCapacity(nextMap);
        saveCachedCapacityMap(nextMap);
      }
      // Extract slug/id → Arabic-name mapping for per-car row labels.
      const nameMap = {};
      for (const c of Array.isArray(carsFull.data) ? carsFull.data : []) {
        const key = c.id || c.car_type;
        const label = c.name_ar || c.name_en || c.name || key;
        if (key) nameMap[key] = label;
      }
      setCarNameById(nameMap);
    } catch {
      toast.error(t("bookingsPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, t]);

  // Merge an updated booking (returned from /admin/bookings/{id} endpoints)
  // into the current list. Falls back to a full reload if we can't locate it.
  const patchLocalBooking = useCallback((updated) => {
    if (!updated || !updated.id) { load(); return; }
    // 2026-02-26 §MultiDayExplode: server-side ids never carry `__DATE`
    // suffixes, so match by REAL id and merge. Handles both split and
    // non-split rows correctly.
    const target = String(updated.id).split("__")[0];
    setBookings((prev) => prev.map((b) => (b.id === target ? { ...b, ...updated, id: b.id } : b)));
  }, [load]);

  // Hard-delete a booking (admin only). Simplified to a single confirm
  // (2026-02-24 §DeleteSimplify) — the previous "type حذف" step was removed.
  // On 200: reverses driver commissions server-side and cascades transactions/
  //   settlements; the local row is removed immediately.
  const [deleteBusy, setDeleteBusy] = useState(false);

  const openDeleteFlow = useCallback(async (b) => {
    if (!b?.id) return;
    // 2026-02-26 §MultiDayExplode: b.id may be `{orig}__{YYYY-MM-DD}` for
    // split rows — strip the suffix before hitting the API and matching
    // local state, otherwise the DELETE returns 404.
    const _real = String(b._original_id || b.id).split("__")[0];
    const seq = b.seq_number || b.booking_number || String(_real).slice(0, 8).toUpperCase();
    const ok = window.confirm(
      `سيتم حذف الحجز ${seq} نهائياً من قاعدة البيانات ولا يمكن التراجع.\n\nهل تريد المتابعة؟`,
    );
    if (!ok) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/admin/bookings/${_real}`);
      setBookings((prev) => prev.filter((x) => x.id !== _real));
      const label = (data && data.seq_number) || seq;
      toast.success(t("bookingsPage.deleteSuccess", { label }));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("bookingsPage.deleteError");
      toast.error(msg);
    } finally {
      setDeleteBusy(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Pre-compute date buckets for counts + filter (Latin ISO YYYY-MM-DD).
  const today = todayISO();
  const yesterday = shiftISO(-1);
  const tomorrow = shiftISO(1);

  const matchesDate = useCallback((b, key) => {
    const d = (b.trip_date || (b.pickup_time || "").slice(0, 10) || "").slice(0, 10);
    if (!d) return false;
    switch (key) {
      case "past": return d < yesterday;
      case "yesterday": return d === yesterday;
      case "today": return d === today;
      case "tomorrow": return d === tomorrow;
      case "future": return d > tomorrow;
      default: return true;
    }
  }, [today, yesterday, tomorrow]);

  // NOTE: `dateCounts` was moved BELOW `flatAll` (2026-02) so it can count
  // at the row-level. See the `dateCounts` memo declared after `flatAll`.

  const statusCounts = useMemo(() => {
    const c = { all: bookings.length };
    STATUS_CHIP_KEYS.filter((k) => k !== "all").forEach((key) => {
      c[key] = bookings.filter((b) => b.status === key).length;
    });
    return c;
  }, [bookings]);

  // Same-client grouping — each booking KEEPS its own row (own driver /
  // status / delete). But every row belonging to the same
  // (user_id, normalized client_name, normalized client_phone) tuple:
  //   • displays the SAME seq (smallest booking_number in the group)
  //   • shows a "حركة N/M" badge indicating its position within the group
  //     when the group has more than one booking.
  const groupInfoMap = useMemo(() => {
    const buckets = new Map();
    for (const b of bookings) {
      const k = _groupKey(b);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(b);
    }
    const info = {};
    for (const arr of buckets.values()) {
      const bySeq = [...arr].sort((a, b) => _seqNumeric(a) - _seqNumeric(b));
      const rep = bySeq[0];
      const groupSeq = rep?.booking_number || rep?.seq_number || null;
      const allSeqs = arr
        .map((x) => x.booking_number || x.seq_number)
        .filter(Boolean);
      const groupSize = arr.length;
      const byDT = [...arr].sort((a, b) => {
        const da = `${a.trip_date || ""} ${a.trip_time || "00:00"}`;
        const db = `${b.trip_date || ""} ${b.trip_time || "00:00"}`;
        return da.localeCompare(db);
      });
      byDT.forEach((b, i) => {
        info[b.id] = {
          groupSeq,
          movement: i + 1,
          groupSize,
          allSeqs,
          groupKey: _groupKey(b),
        };
      });
    }
    return info;
  }, [bookings]);

  // 2026-02-26 §MultiDayExplode: bookings whose routes[] span 2+ DIFFERENT
  // trip_dates are exploded into ONE virtual booking PER date, so each date
  // shows up in its own date-chip tab and the counts reflect "booking-dates"
  // rather than the raw row count. Each virtual booking:
  //   • id = `{origId}__{YYYY-MM-DD}` — unique for React key + inline-edit
  //     testids. Callers MUST strip the suffix (see `realBookingId()`)
  //     before hitting any backend endpoint.
  //   • _original_id = original booking id — used by openBooking / delete /
  //     driver assignment / inline PATCH.
  //   • routes[] — only routes matching that date. Preserves per-route
  //     financials + flight info.
  //   • trip_date/trip_time/pickup_location/destination bubble up from the
  //     first route on that date so single-date UI still works.
  //   • total_price = sum of route_totals ON that date only (accurate
  //     per-date sub-total in the table).
  // Bookings with 0-1 routes (or all routes on the same date) pass through
  // untouched — no explosion.
  const realBookingId = useCallback((id) => {
    if (!id) return id;
    const s = String(id);
    const i = s.indexOf("__");
    return i > 0 ? s.slice(0, i) : s;
  }, []);

  const explodedBookings = useMemo(() => {
    // 2026-07 §CompanyBookingsFilter: honour the URL filter before explode
    // to keep memoised list small. Bookings expose the owning company as
    // `company_id` (normalized on the API side); `user_id` is stripped by
    // the admin serializer. Match on either just in case.
    const source = filterCompanyId
      ? bookings.filter(
          (b) => (b.company_id || b.user_id) === filterCompanyId,
        )
      : bookings;
    const out = [];
    for (const b of source) {
      const routes = Array.isArray(b.routes) ? b.routes : [];
      if (routes.length <= 1) { out.push(b); continue; }
      const byDate = {};
      routes.forEach((r, origIdx) => {
        const d = String(r?.trip_date || r?.date || b.trip_date || "").slice(0, 10);
        if (!d) return;
        // 2026-02-28 §RealRouteIdx: preserve the ORIGINAL index in
        // routes[] so per-route mutations (driver-assign, financials)
        // target the correct leg in the un-split booking.
        (byDate[d] = byDate[d] || []).push({ ...r, _originalRouteIdx: origIdx });
      });
      const dates = Object.keys(byDate);
      if (dates.length <= 1) { out.push(b); continue; }
      // Preserve doc order of dates as they appear in routes[].
      const ordered = [];
      const seen = new Set();
      for (const r of routes) {
        const d = String(r?.trip_date || r?.date || b.trip_date || "").slice(0, 10);
        if (!d || seen.has(d)) continue;
        seen.add(d);
        ordered.push(d);
      }
      for (const d of ordered) {
        const rs = byDate[d];
        const first = rs[0] || {};
        const dateTotal = rs.reduce((s, r) => s + Number(r?.route_total || 0), 0);
        out.push({
          ...b,
          _original_id: b.id,
          id: `${b.id}__${d}`,
          _split_from_multi_day: true,
          _split_date: d,
          trip_date: d,
          trip_time: first.trip_time || first.time || b.trip_time,
          pickup_location: first.pickup_location || b.pickup_location,
          destination: first.destination || b.destination,
          // 2026-02-27 §PerDateFlightOverride: without this, `{...b}` above
          // would leak the BOOKING-LEVEL flight_number/airline to every
          // split row, so a Saturday route (SV123) + Monday route (SV456)
          // would both show the same flight number in the table. Override
          // with the FIRST route's flight of this date (routes on the same
          // date typically share the same flight; flatAll further refines
          // per-route when a date has >1 route).
          flight_number: first.flight_number ?? b.flight_number ?? null,
          flight_airline: first.flight_airline ?? b.flight_airline ?? null,
          routes: rs,
          // Per-date sub-total (only routes on this date). Falls back to the
          // booking's total_price when route_total is missing.
          total_price: dateTotal > 0 ? dateTotal : Number(b.total_price || 0),
          price: dateTotal > 0 ? dateTotal : Number(b.price || b.total_price || 0),
        });
      }
    }
    return out;
  }, [bookings, filterCompanyId]);

  const filtered = useMemo(() => {
    // 2026-02 §RowDateFilter: date filtering was moved OUT of this memo and
    // applied at the FLAT-ROW level in `flatBookings` below. That way a
    // booking whose route[0] is tomorrow and route[1] is the day after can
    // show route[0] under "tomorrow" chip and route[1] under "future" chip,
    // instead of dumping both under the booking's header date.
    let list = explodedBookings;
    if (statusChip && statusChip !== "all") list = list.filter((b) => b.status === statusChip);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const qDigits = q.replace(/[^\d]/g, "");
      // Client-group-aware seq search: find any group whose members contain a
      // seq matching the query, then show ALL rows of that group.
      const matchingGroups = new Set();
      for (const b of list) {
        const info = groupInfoMap[b.id];
        const seqs = info?.allSeqs || [b.booking_number, b.seq_number].filter(Boolean);
        if (seqs.some((s) => String(s || "").toLowerCase().includes(q))) {
          matchingGroups.add(info?.groupKey || `solo:${b.id}`);
        }
      }
      list = list.filter((b) => {
        const info = groupInfoMap[b.id];
        const gKey = info?.groupKey || `solo:${b.id}`;
        if (matchingGroups.has(gKey)) return true;
        if ((b.company_name || "").toLowerCase().includes(q)) return true;
        if ((b.client_name || "").toLowerCase().includes(q)) return true;
        if (qDigits && String(b.client_phone || "").replace(/[^\d]/g, "").includes(qDigits)) return true;
        if ((b.pickup_location || "").toLowerCase().includes(q)) return true;
        if ((b.destination || b.dropoff_location || "").toLowerCase().includes(q)) return true;
        return false;
      });
    }
    return list;
  }, [explodedBookings, statusChip, search, groupInfoMap]);

  // UX RULE (2026-07): bookings are ALWAYS displayed as "مؤكد" — no red
  // overlays for legacy rejected/cancelled rows.
  const isCancelled = () => false;

  // Per-company movement index (حركة N): reset per company, sorted by trip
  // date+time ascending, so within a company's visible bookings the earliest
  // is "1" and the latest is "N". Different companies are numbered
  // independently, and admin-created bookings without user_id fall back to a
  // "manual:<client name>" bucket.
  const movementIndexMap = useMemo(() => {
    const out = {};
    const byCompany = {};
    for (const b of filtered) {
      const key = b.user_id || b.company_id || `manual:${b.client_name || b.customer_name || ""}`;
      (byCompany[key] = byCompany[key] || []).push(b);
    }
    for (const arr of Object.values(byCompany)) {
      arr
        .slice()
        .sort((a, x) => {
          const A = `${a.trip_date || ""} ${a.trip_time || "00:00"}`;
          const X = `${x.trip_date || ""} ${x.trip_time || "00:00"}`;
          return A.localeCompare(X);
        })
        .forEach((b, i) => { out[b.id] = i + 1; });
    }
    return out;
  }, [filtered]);

  // Last-visited booking — highlights the card/row the admin came back from
  // so they don't lose their place in a long list.
  const [lastVisitedId, setLastVisitedId] = useState(
    () => (typeof window !== "undefined" ? localStorage.getItem("cb.lastVisitedBookingId") : null),
  );
  const openBooking = useCallback((id) => {
    try { localStorage.setItem("cb.lastVisitedBookingId", id); } catch {}
    setLastVisitedId(id);
    navigate(`/booking/${id}`);
  }, [navigate]);

  // FLAT LIST with per-route expansion (2026-07-22 v3.3).
  // A booking with N routes emits N rows so every leg gets its own line in
  // the table. Each expanded row carries `_route` (the specific leg) +
  // `_routeIdx` (0-based) + `_routeTotal`. When the booking has 0 or 1 route
  // the row is emitted unchanged (no `_route` marker).
  // Helper: resolve a car's admin-configured max seats. Falls back to prefix
  // matching so legacy slugs like `gmc_yukon_denali` still map to `gmc` (7).
  const resolveCarCapacity = useCallback((slug) => {
    if (!slug) return null;
    if (carsCapacity[slug]) return carsCapacity[slug];
    const first = String(slug).split("_")[0];
    if (first && carsCapacity[first]) return carsCapacity[first];
    return null;
  }, [carsCapacity]);

  const flatAll = useMemo(() => {
    const out = [];
    for (const b of filtered) {
      const routes = Array.isArray(b.routes) ? b.routes : [];
      // Multi-route bookings — expand into one row per (route × car_type).
      // Spec 2026-07-24 v6 §PerCarRow: when a single leg carries 2+ car
      // types (e.g. Route 2: gmc × 1 / ford × 1), each type gets its OWN
      // row so the admin sees per-car pricing (450 vs 300) instead of a
      // single "750 aggregate" that hides which car cost what.
      if (routes.length > 1) {
        // 2026-02-24 §MultiDaySplit (Hybrid): pre-compute per-date route
        // counts so each generated row can carry `_dateRouteIdx` /
        // `_dateRouteTotal` — the "+N مسار" badge and the split-by-date
        // filter use these. Same-date multi-car routes STILL expand
        // per-car (v6 behaviour preserved) so driver assignment stays
        // per-car in the table.
        const dateBuckets = {};        // date → array of route indices in doc order
        routes.forEach((rt, ri) => {
          const dd = String(rt?.trip_date || rt?.date || b.trip_date || "").slice(0, 10);
          if (!dd) return;
          (dateBuckets[dd] = dateBuckets[dd] || []).push(ri);
        });
        routes.forEach((r, idx) => {
          const rowDate = String(r?.trip_date || r?.date || b.trip_date || "").slice(0, 10);
          const bucket = dateBuckets[rowDate] || [];
          const dateRouteIdx = bucket.indexOf(idx);
          const dateRouteTotal = bucket.length || 1;
          const cars = Array.isArray(r?.cars) && r.cars.length ? r.cars : [{
            car_type: r?.car_type || b.car_type,
            quantity: r?.quantity || r?.cars_qty || b.cars_qty || 1,
          }];
          cars.forEach((c, carIdx) => {
            const carQty = Number(c.quantity) || 0;
            // Per-car row price: prefer stored line_total, else unit_price × qty,
            // else fall back to route_total when only one car in the route.
            const carPrice =
              c.line_total != null
                ? Number(c.line_total)
                : c.unit_price != null
                ? Number(c.unit_price) * carQty
                : cars.length === 1
                ? r?.route_total ?? b.total_price
                : 0;
            // FIX 2026-02 §MultiCarLabel: previously we spread `...b` which
            // carried the header's `car_type_name` (Arabic name for the
            // booking-level car). That made every per-car row for a
            // multi-car route show the SAME parent label ("جمس"), hiding
            // the fact that Route 2 was actually Ford + GMC. Resolve the
            // per-car Arabic name from `carNameById` (built from /api/cars).
            // Fallback order:
            //   1. carNameById[slug]       — /cars catalog (source of truth)
            //   2. booking.car_type_name   — only when slug == booking.car_type
            //      (handles legacy slugs like `gmc_yukon_denali` that aren't
            //      in the current catalog but still exist on the booking).
            //   3. slug itself             — last resort.
            const carSlug = c.car_type || b.car_type;
            const perCarName =
              carNameById[carSlug] ||
              (carSlug === b.car_type ? b.car_type_name || b.car_name_ar : null) ||
              carSlug;
            // Per-car passenger CAPACITY (2026-02): user asked for each row
            // to display the car's admin-configured max seats (7 for GMC,
            // 3 for Ford, etc.) instead of the booking's total pax.
            const carCapacity = resolveCarCapacity(carSlug);
            // 2026-07-28 §WageIsolationFix: pull ONLY from per-car → per-route.
            // Previous fallback to `b.driver_wage` (booking-level aggregate)
            // caused the "wage propagation illusion": setting wage on route 0
            // made booking.driver_wage = 110 (sum), and every other exploded
            // row that had no per-car wage would fall back to that 110 and
            // display it — even though the actual stored wage on those routes
            // was null. Fix: no aggregate fallback here. Rows without a
            // per-car / per-route wage display 0.
            const carWage = (c && c.driver_wage != null)
              ? Number(c.driver_wage)
              : (r && r.driver_wage != null)
              ? Number(r.driver_wage)
              : 0;
            // 2026-02-28 §DriverIsolationDeep: resolve driver ONLY from per-car
            // → per-route. NEVER inherit from booking-level. Previous versions
            // relied on downstream display code to strip the leak, but any
            // component that spread `{...b}` still saw the booking-level
            // driver_* fields. Materialise the correct values here so every
            // consumer sees the isolated view.
            const rowDriver = (c && (c.driver_id || c.driver_name || c.external_driver_name)) ? {
              driver_id: c.driver_id ?? null,
              driver_name: c.driver_name ?? null,
              driver_phone: c.driver_phone ?? null,
              plate_number: c.plate_number ?? null,
              external_driver_name: c.external_driver_name ?? null,
              external_driver_phone: c.external_driver_phone ?? null,
              external_driver_id: c.external_driver_id ?? null,
            } : (r && (r.driver_id || r.driver_name || r.external_driver_name)) ? {
              driver_id: r.driver_id ?? null,
              driver_name: r.driver_name ?? null,
              driver_phone: r.driver_phone ?? null,
              plate_number: r.plate_number ?? null,
              external_driver_name: r.external_driver_name ?? null,
              external_driver_phone: r.external_driver_phone ?? null,
              external_driver_id: r.external_driver_id ?? null,
            } : {
              driver_id: null,
              driver_name: null,
              driver_phone: null,
              plate_number: null,
              external_driver_name: null,
              external_driver_phone: null,
              external_driver_id: null,
            };
            out.push({
              ...b,
              // 2026-02-26 §MultiDayExplode: honour `_original_id` from the
              // exploded virtual booking. Overwriting it with `b.id` here
              // would leak the split composite id back into the click /
              // delete / PATCH paths and break routing to /booking/:id.
              _original_id: b._original_id || b.id,
              _route: r,
              // 2026-02-28 §RealRouteIdx: for multi-day-exploded parents,
              // the virtual `routes` array is a filtered subset — use the
              // preserved `_originalRouteIdx` so per-route mutations target
              // the correct leg in the un-split booking.
              _routeIdx: (r && r._originalRouteIdx != null) ? r._originalRouteIdx : idx,
              _routeTotal: routes.length,
              // 2026-02-24 §MultiDaySplit: route position/count WITHIN this
              // row's date. "+N مسار" badge = _dateRouteTotal.
              _dateRouteIdx: dateRouteIdx,
              _dateRouteTotal: dateRouteTotal,
              _carIdx: carIdx,
              _carTotal: cars.length,
              _car: c,
              pickup_location: r?.pickup_location || b.pickup_location,
              destination: r?.destination || b.destination,
              trip_date: r?.trip_date || r?.date || b.trip_date,
              trip_time: r?.trip_time || r?.time || b.trip_time,
              pickup_time: null,
              car_type: carSlug,
              car_type_name: perCarName,
              cars_qty: carQty || 1,
              passengers: carCapacity || b.passengers,
              price: carPrice,
              total_price: carPrice,
              driver_wage: carWage,
              // 2026-02-24 §PerRouteFlight: each row shows ITS OWN route's
              // flight info — no fallback to booking-level so a FZ 873 header
              // never bleeds into other routes.
              flight_number: r?.flight_number ?? null,
              flight_airline: r?.flight_airline ?? null,
              // 2026-02-26 §PerRouteFinancials: same per-route isolation for
              // commission + cash. Row shows ONLY the route's own values —
              // never the booking-level fallback (that would double-count
              // when summing rows in reports).
              agent_commission: Number(r?.agent_commission || 0) || null,
              cash_from_client: Number(r?.cash_from_client || 0) || null,
              // 2026-02-28 §RoutePaid: expose per-route paid amount so the
              // Company Bookings table can render an editable "المدفوع"
              // column per exploded row. Only the FIRST car of a route
              // carries the paid value (paid is stored per-route, not
              // per-car); other cars in the same route show 0 to avoid
              // double-counting.
              paid_amount: (carIdx === 0)
                ? Number(r?.paid_amount || 0)
                : 0,
              // 2026-02-28 §PaymentMethod: per-route tag (transfer/cash/…).
              // Only the FIRST car surfaces it (same reason as paid_amount).
              payment_method: (carIdx === 0)
                ? (r?.payment_method || "")
                : "",
              // 2026-02-28 §DriverIsolationDeep: overwrite driver_* so the
              // `{...b}` spread above cannot leak booking-level values.
              ...rowDriver,
            });
          });
        });
      } else {
        // Single-route booking. If it has multiple car types, ALSO expand
        // them into per-car rows (same UX rule as multi-route).
        const r0 = routes[0] || {};
        const cars = Array.isArray(r0.cars) && r0.cars.length ? r0.cars : null;
        if (cars && cars.length > 1) {
          cars.forEach((c, carIdx) => {
            const carQty = Number(c.quantity) || 0;
            const carPrice =
              c.line_total != null
                ? Number(c.line_total)
                : c.unit_price != null
                ? Number(c.unit_price) * carQty
                : 0;
            const carSlug = c.car_type || b.car_type;
            const perCarName =
              carNameById[carSlug] ||
              (carSlug === b.car_type ? b.car_type_name || b.car_name_ar : null) ||
              carSlug;
            const carCapacity = resolveCarCapacity(carSlug);
            // 2026-07-28 §WageIsolationFix — see multi-route branch above.
            // No fallback to booking-level aggregate to prevent the "wage
            // propagation illusion" when the booking has multiple cars.
            const carWage = (c && c.driver_wage != null)
              ? Number(c.driver_wage)
              : (r0 && r0.driver_wage != null)
              ? Number(r0.driver_wage)
              : 0;
            // 2026-02-28 §DriverIsolationDeep — same isolation rule for the
            // single-route/multi-car branch. Only per-car driver fields
            // survive the spread; legacy `b.driver_*` never leaks.
            const rowDriver2 = (c && (c.driver_id || c.driver_name || c.external_driver_name)) ? {
              driver_id: c.driver_id ?? null,
              driver_name: c.driver_name ?? null,
              driver_phone: c.driver_phone ?? null,
              plate_number: c.plate_number ?? null,
              external_driver_name: c.external_driver_name ?? null,
              external_driver_phone: c.external_driver_phone ?? null,
              external_driver_id: c.external_driver_id ?? null,
            } : (r0 && (r0.driver_id || r0.driver_name || r0.external_driver_name)) ? {
              driver_id: r0.driver_id ?? null,
              driver_name: r0.driver_name ?? null,
              driver_phone: r0.driver_phone ?? null,
              plate_number: r0.plate_number ?? null,
              external_driver_name: r0.external_driver_name ?? null,
              external_driver_phone: r0.external_driver_phone ?? null,
              external_driver_id: r0.external_driver_id ?? null,
            } : {
              driver_id: null,
              driver_name: null,
              driver_phone: null,
              plate_number: null,
              external_driver_name: null,
              external_driver_phone: null,
              external_driver_id: null,
            };
            out.push({
              ...b,
              _route: r0,
              _routeIdx: 0,
              _routeTotal: 1,
              _carIdx: carIdx,
              _carTotal: cars.length,
              _car: c,
              trip_date: r0.trip_date || r0.date || b.trip_date,
              trip_time: r0.trip_time || r0.time || b.trip_time,
              pickup_time: r0.trip_time ? null : b.pickup_time,
              car_type: carSlug,
              car_type_name: perCarName,
              cars_qty: carQty || 1,
              passengers: carCapacity || b.passengers,
              price: carPrice,
              total_price: carPrice,
              driver_wage: carWage,
              // 2026-02-24 §PerRouteFlight — single-route bookings also honour
              // route-level flight fields when present; else fall back to
              // booking-level (legacy single-leg bookings).
              flight_number: r0.flight_number ?? b.flight_number ?? null,
              flight_airline: r0.flight_airline ?? b.flight_airline ?? null,
              // 2026-02-26 §PerRouteFinancials: single-route → prefer
              // per-route financials, fall back to booking-level (legacy).
              agent_commission:
                Number(r0?.agent_commission || 0) > 0
                  ? Number(r0.agent_commission)
                  : Number(b.agent_commission || 0) || null,
              cash_from_client:
                Number(r0?.cash_from_client || 0) > 0
                  ? Number(r0.cash_from_client)
                  : Number(b.cash_from_client || 0) || null,
              // 2026-02-28 §DriverIsolationDeep: overwrite driver_* fields.
              ...rowDriver2,
            });
          });
        } else {
          // Truly single-car booking — one row.
          // 2026-02-28 §DriverIsolationSingleCar: multi-day-exploded rows
          // reach this branch with `routes = [only-this-date-route]`. When the
          // admin assigned a driver via `route_idx`, backend clears the
          // booking-root driver_* fields, so `...b` spreads NULLs. We must
          // extract driver + wage from routes[0].cars[0] → routes[0] → root
          // (legacy). Also expose `_route/_car/_routeIdx/_carIdx` so the
          // display cell + assignment modal address the correct nested slot.
          // 2026-02-28 §NoRootMirrorLeak: for exploded rows (multi-day split)
          // NEVER fall back to booking-root driver_* — the backend mirrors
          // route[0]'s driver onto the root for legacy readers, which would
          // make every OTHER date's row inherit route[0]'s driver (the exact
          // bug the user reported on MZ-2026-000611: Wed→Husain leaked to
          // next-Tue row visually, appearing as "auto-assignment").
          const isExplodedSplit = !!b._split_from_multi_day;
          // Also treat non-exploded bookings that HAVE a multi-route routes[]
          // array (legacy? shouldn't happen, but just in case) similarly —
          // any time the row is scoped to a specific leg of a multi-leg trip,
          // root-fallback lies.
          const c0 = Array.isArray(r0.cars) && r0.cars.length ? r0.cars[0] : null;
          const allowRootFallback = !isExplodedSplit;
          const rowDriver3 = (c0 && (c0.driver_id || c0.driver_name || c0.external_driver_name)) ? {
            driver_id: c0.driver_id ?? null,
            driver_name: c0.driver_name ?? null,
            driver_phone: c0.driver_phone ?? null,
            plate_number: c0.plate_number ?? null,
            external_driver_name: c0.external_driver_name ?? null,
            external_driver_phone: c0.external_driver_phone ?? null,
            external_driver_id: c0.external_driver_id ?? null,
          } : (r0 && (r0.driver_id || r0.driver_name || r0.external_driver_name)) ? {
            driver_id: r0.driver_id ?? null,
            driver_name: r0.driver_name ?? null,
            driver_phone: r0.driver_phone ?? null,
            plate_number: r0.plate_number ?? null,
            external_driver_name: r0.external_driver_name ?? null,
            external_driver_phone: r0.external_driver_phone ?? null,
            external_driver_id: r0.external_driver_id ?? null,
          } : allowRootFallback ? {
            driver_id: b.driver_id ?? null,
            driver_name: b.driver_name ?? null,
            driver_phone: b.driver_phone ?? null,
            plate_number: b.plate_number ?? null,
            external_driver_name: b.external_driver_name ?? null,
            external_driver_phone: b.external_driver_phone ?? null,
            external_driver_id: b.external_driver_id ?? null,
          } : {
            driver_id: null,
            driver_name: null,
            driver_phone: null,
            plate_number: null,
            external_driver_name: null,
            external_driver_phone: null,
            external_driver_id: null,
          };
          // Wage: per-car → per-route → booking root (legacy, only when NOT
          // an exploded row — root wage is a SUM across all legs and would
          // over-report the per-date total).
          const rowWage = (c0 && c0.driver_wage != null)
            ? Number(c0.driver_wage)
            : (r0 && r0.driver_wage != null)
            ? Number(r0.driver_wage)
            : (allowRootFallback && b.driver_wage != null ? Number(b.driver_wage) : 0);
          out.push({
            ...b,
            trip_date: r0.trip_date || r0.date || b.trip_date,
            trip_time: r0.trip_time || r0.time || b.trip_time,
            pickup_time: r0.trip_time ? null : b.pickup_time,
            // Preserve route/car handles so the display cell + modal target
            // routes[0].cars[0] (not the empty booking root).
            _route: r0,
            // 2026-02-28 §RealRouteIdx: exploded rows must map to the real
            // route index in the parent booking; standalone single-route
            // bookings use 0.
            _routeIdx: (r0 && r0._originalRouteIdx != null) ? r0._originalRouteIdx : 0,
            _routeTotal: 1,
            _carIdx: 0,
            _carTotal: 1,
            _car: c0 || null,
            // Per-route financials — per-route wins over legacy root.
            agent_commission:
              Number(r0?.agent_commission || 0) > 0
                ? Number(r0.agent_commission)
                : Number(b.agent_commission || 0) || null,
            cash_from_client:
              Number(r0?.cash_from_client || 0) > 0
                ? Number(r0.cash_from_client)
                : Number(b.cash_from_client || 0) || null,
            // Multi-day-exploded rows need their per-date route's own paid
            // amount + payment method (else editing writes to the wrong leg).
            ...(isExplodedSplit ? {
              paid_amount: Number(r0?.paid_amount || 0),
              payment_method: r0?.payment_method || "",
            } : {}),
            // 2026-02-28 §DriverIsolationSingleCar: overwrite driver_* +
            // driver_wage from the resolved per-route/per-car values so the
            // row NEVER inherits a stale booking-root driver.
            driver_wage: rowWage,
            ...rowDriver3,
          });
        }
      }
    }
    return out;
  }, [filtered, carNameById, resolveCarCapacity]);

  // Row-level date filter (2026-02 §RowDateFilter). Each flat row carries
  // its own `trip_date` (per-route when expanded), so a booking whose route
  // 1 is tomorrow and route 2 is the day after is filtered CORRECTLY —
  // route 1 shows under "غداً" chip, route 2 under "المستقبل".
  const rowMatchesDate = useCallback((row) => {
    const d = (row.trip_date || (row.pickup_time || "").slice(0, 10) || "").slice(0, 10);
    if (rangeActive) {
      if (!d) return false;
      if (rangeFrom && d < rangeFrom) return false;
      if (rangeTo && d > rangeTo) return false;
      return true;
    }
    if (specificDate) {
      return d === specificDate;
    }
    if (dateChip) {
      if (!d) return false;
      switch (dateChip) {
        case "past": return d < yesterday;
        case "yesterday": return d === yesterday;
        case "today": return d === today;
        case "tomorrow": return d === tomorrow;
        case "future": return d > tomorrow;
        default: return true;
      }
    }
    return true;
  }, [dateChip, specificDate, rangeActive, rangeFrom, rangeTo, today, yesterday, tomorrow]);

  const flatBookings = useMemo(
    () => flatAll.filter(rowMatchesDate),
    [flatAll, rowMatchesDate],
  );

  // 2026-02-26 §MultiDayExplode: count at the BOOKING-DATE level using
  // `explodedBookings` (one virtual booking per date). Previously counted
  // flatAll rows which multiplied by cars/routes, giving misleading counts
  // for the date chips (e.g. a 2-car route showed "today: 2" instead of 1).
  const dateCounts = useMemo(() => {
    const c = {};
    DATE_CHIP_KEYS.forEach((key) => { c[key] = 0; });
    for (const b of explodedBookings) {
      for (const key of DATE_CHIP_KEYS) {
        if (matchesDate(b, key)) c[key] += 1;
      }
    }
    return c;
  }, [explodedBookings, matchesDate]);

  // 2026-07 §CompanyBookingsAutoTab: when navigating with `filter_company_id`
  // the default date-chip may hide all of the company's rows (e.g. all
  // bookings are in the past). Auto-pick the first non-empty tab exactly
  // once so the admin lands on a populated view.
  const [autoScopeApplied, setAutoScopeApplied] = useState(false);
  useEffect(() => {
    if (!filterCompanyId || autoScopeApplied) return;
    if (explodedBookings.length === 0) return; // wait for data
    // If current chip already has rows, keep it (respects user preference).
    if (dateChip && dateCounts[dateChip] > 0) {
      setAutoScopeApplied(true);
      return;
    }
    // Otherwise, prefer today > tomorrow > future > past > yesterday.
    const preferred = ["today", "tomorrow", "future", "past", "yesterday"];
    const picked = preferred.find((k) => dateCounts[k] > 0);
    if (picked && picked !== dateChip) setDateChip(picked);
    setAutoScopeApplied(true);
  }, [filterCompanyId, explodedBookings.length, dateCounts, dateChip, autoScopeApplied]);

  // Booking-level list for the MOBILE card view. A booking is included if
  // ANY of its flat rows (any route) passes the current date filter — so a
  // multi-route booking whose route 2 falls on the selected date still
  // shows on mobile even if route 1 doesn't.
  const bookingsForMobile = useMemo(() => {
    const ids = new Set(flatBookings.map((r) => r.id));
    return filtered.filter((b) => ids.has(b.id));
  }, [filtered, flatBookings]);

  // Sort bookings by the selected column.
  const NUMERIC_SORT_KEYS = new Set(["internal_serial", "passengers", "price", "driver_wages", "net", "day_of_week", "cash_from_client", "agent_commission"]);
  const getSortValue = useCallback((b, key) => {
    const p = b || {};
    switch (key) {
      case "seq_number": return p.booking_number || p.seq_number || "";
      case "internal_serial": return Number(p.internal_serial ?? 0);
      case "company": return p.company_name || "";
      case "client_name": return p.client_name || p.user_name || "";
      case "client_phone": return p.client_phone || "";
      case "trip_date": return p.trip_date || (p.pickup_time || "").slice(0, 10) || "";
      case "day_of_week": {
        // Weekday index 0..6 (Sun..Sat) — same rule used by arabicDayOfWeek.
        const raw = String(p.trip_date || (p.pickup_time || "").slice(0, 10) || "").trim();
        if (!raw) return -1;
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? -1 : d.getDay();
      }
      case "trip_time": return p.trip_time || (p.pickup_time || "").slice(11, 16) || "";
      case "pickup_location": return p.pickup_location || "";
      case "destination": return p.destination || p.dropoff_location || "";
      case "car_type": return p.car_type_name || p.car_type || "";
      case "passengers": return Number(p.passengers || 0);
      case "price": return Number(p.price || p.total_price || 0);
      case "cash_from_client": return Number(p.cash_from_client || 0);
      case "agent_commission": return Number(p.agent_commission || 0);
      case "driver_wages": return Number(p.driver_wage || 0);
      case "net":
        return Number(p.price || p.total_price || 0) - Number(p.driver_wage || 0) - Number(p.agent_commission || 0);
      case "status": return p.status || "";
      case "flight_number": return getBookingFlightNumber(p);
      case "flight_airline": return getBookingAirline(p);
      case "airline": return getBookingAirline(p);
      default: return "";
    }
  }, []);

  const sortedBookings = useMemo(() => {
    if (!sortBy) return flatBookings;
    const isNumeric = NUMERIC_SORT_KEYS.has(sortBy);
    const dir = sortDir === "desc" ? -1 : 1;
    const copy = [...flatBookings];
    copy.sort((a, b) => {
      const va = getSortValue(a, sortBy);
      const vb = getSortValue(b, sortBy);
      if (isNumeric) return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
      return String(va).localeCompare(String(vb), "ar") * dir;
    });
    return copy;
  }, [flatBookings, sortBy, sortDir, getSortValue]);

  const toggleSort = (col) => {
    if (sortBy !== col) {
      setSortBy(col);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="px-4 py-6">
        <div className="max-w-full mx-auto">
          {/* 2026-07 §CompanyBookingsFilter: banner shown when filter is active */}
          {filterCompanyId && (
            <div
              className="mb-4 rounded-2xl border-2 border-primary/60 bg-gradient-to-l from-primary/15 to-primary/5 p-3 md:p-4 flex items-center justify-between flex-wrap gap-3"
              data-testid="company-filter-banner"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-primary/20 border border-primary/50 text-primary flex items-center justify-center shrink-0">
                  <span style={{ fontSize: 20 }}>🏢</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-base truncate" data-testid="company-filter-name">
                    حجوزات: <span className="text-primary">{filterCompanyName || filterCompanyId}</span>
                  </div>
                  <div className="text-white/60 text-xs mt-0.5 num" dir="ltr">
                    {fmtNum(explodedBookings.length)} حجز · إجمالي:{" "}
                    {fmtRiyal(
                      explodedBookings.reduce((s, b) => s + Number(b.total_price || 0), 0),
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSearchParams({})}
                data-testid="company-filter-clear"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-primary/50 text-primary hover:bg-primary/10 text-xs font-bold"
              >
                <X size={14} weight="bold" /> إلغاء التصفية
              </button>
            </div>
          )}
          <div className="flex items-end justify-between mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {isAdmin ? t("bookingsPage.titleAdmin") : t("bookingsPage.titleUser")}{" "}
              <span className="text-primary text-lg num">({fmtNum(sortedBookings.length)})</span>
            </h1>
            <button
              type="button"
              onClick={() => {
                if (!sortedBookings.length) { toast.error(t("bookingsPage.exportEmpty")); return; }
                try {
                  exportBookingsToExcel(sortedBookings, {
                    filename: `bookings-${new Date().toISOString().slice(0, 10)}.xlsx`,
                  });
                  toast.success(t("bookingsPage.exportOk", { count: fmtNum(sortedBookings.length) }));
                } catch (e) {
                  toast.error(t("bookingsPage.exportError"));
                }
              }}
              data-testid="btn-export-excel"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6F42]/15 hover:bg-[#1D6F42]/25 border border-[#1D6F42]/60 text-[#5CD8A0] text-sm font-semibold transition-all"
            >
              <MicrosoftExcelLogo size={18} weight="fill" />
              {t("bookingsPage.exportExcel")}
            </button>
            {/* Zoom controls: scale the table container for mobile / large screens. */}
            <div
              data-testid="zoom-controls"
              className="hidden md:inline-flex items-center gap-0 rounded-full bg-primary/10 border border-primary/40 overflow-hidden"
              dir="ltr"
            >
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(0.6, Math.round((z - 0.1) * 10) / 10))}
                disabled={zoom <= 0.6}
                title={t("bookingsPage.zoomOut")}
                data-testid="zoom-out"
                className="w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <MagnifyingGlassMinus size={14} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => setZoom(1.0)}
                title={t("bookingsPage.zoomReset")}
                data-testid="zoom-reset"
                className="min-w-[46px] h-8 px-2 text-primary text-[11px] font-bold num border-x border-primary/25 hover:bg-primary/15 transition-colors"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(1.5, Math.round((z + 0.1) * 10) / 10))}
                disabled={zoom >= 1.5}
                title={t("bookingsPage.zoomIn")}
                data-testid="zoom-in"
                className="w-8 h-8 flex items-center justify-center text-primary hover:bg-primary/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <MagnifyingGlassPlus size={14} weight="bold" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-xl">
              <MagnifyingGlass size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("bookingsPage.searchPlaceholder")}
                className="mz-input pr-9"
                data-testid="filter-search"
              />
            </div>
          </div>

          {/* Specific date range — cumulative with search & status; disables quick chips */}
          <div className="mb-3 rounded-2xl border border-primary/20 bg-card/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-primary font-bold text-sm">📅 {t("bookingsPage.dateRangeLabel")}</span>
              {rangeActive && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-bold">{t("bookingsPage.rangeActive")}</span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <div className="text-xs text-white/60 mb-1 text-right">{t("bookingsPage.rangeFrom")}</div>
                <CustomDatePicker value={rangeFrom} onChange={setRangeFrom} placeholder="YYYY-MM-DD" testId="range-from" />
              </div>
              <div>
                <div className="text-xs text-white/60 mb-1 text-right">{t("bookingsPage.rangeTo")}</div>
                <CustomDatePicker value={rangeTo} onChange={setRangeTo} placeholder="YYYY-MM-DD" testId="range-to" />
              </div>
              {rangeActive && (
                <button
                  type="button"
                  onClick={() => { setRangeFrom(""); setRangeTo(""); }}
                  data-testid="range-clear"
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-destructive/50 text-destructive hover:bg-destructive/10 text-xs font-bold"
                >
                  <X size={13} weight="bold" /> {t("bookingsPage.clearRange")}
                </button>
              )}
            </div>
          </div>

          {/* Date filter row */}
          <div className={`mb-3 transition-opacity ${rangeActive ? "opacity-40 pointer-events-none" : ""}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-primary font-bold text-sm">📅 {t("bookingsPage.dateLabel")}</span>
              {rangeActive && (
                <span className="text-white/50 text-[11px]">{t("bookingsPage.dateDisabled")}</span>
              )}
            </div>
            <div
              dir={isEn ? "ltr" : "rtl"}
              data-testid="date-chips"
              className="flex flex-row items-center flex-wrap gap-2"
            >
              {DATE_CHIP_KEYS.map((key) => {
                const active = dateChip === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`chip-date-${key}`}
                    onClick={() => { setDateChip(active ? "" : key); setSpecificDate(""); }}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-bold"
                        : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                    }`}
                  >
                    <span>{t(`bookingsPage.dateChips.${key}`)}</span>
                    <span
                      className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                      }`}
                    >
                      {fmtNum(dateCounts[key] || 0)}
                    </span>
                  </button>
                );
              })}
              {/* Specific date picker chip — English calendar via react-day-picker (enUS locale) */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="chip-specific-date-wrapper"
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm border transition-all whitespace-nowrap cursor-pointer ${
                      specificDate
                        ? "bg-primary text-primary-foreground border-primary font-bold"
                        : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                    }`}
                  >
                    <CalendarBlank size={14} weight="duotone" />
                    <span className="text-sm">{t("bookingsPage.specificDate")}</span>
                    {specificDate && (
                      <span className="num text-[11px] font-bold ltr-input bg-[#0A1936]/25 text-primary-foreground px-2 py-0.5 rounded-full" dir="ltr">
                        {specificDate}
                      </span>
                    )}
                    {specificDate && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSpecificDate(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setSpecificDate(""); } }}
                        data-testid="chip-specific-date-clear"
                        title="Clear"
                        className="w-4 h-4 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center"
                      >
                        <X size={9} weight="bold" />
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-auto p-0 bg-card border-primary/30"
                  dir="ltr"
                  lang="en"
                >
                  <Calendar
                    mode="single"
                    locale={enUS}
                    weekStartsOn={0}
                    selected={specificDate ? new Date(specificDate + "T00:00:00") : undefined}
                    onSelect={(d) => {
                      if (d) {
                        setSpecificDate(fmtFns(d, "yyyy-MM-dd"));
                        setDateChip("");
                      }
                    }}
                    data-testid="chip-specific-date-calendar"
                    className="bg-card text-white [&_.rdp]:text-white [&_.rdp-caption_label]:text-primary [&_.rdp-head_cell]:text-primary/70 [&_.rdp-day]:text-white [&_.rdp-day_selected]:!bg-primary [&_.rdp-day_selected]:!text-primary-foreground [&_.rdp-day_today]:text-primary [&_button]:hover:bg-primary/20"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Status filter row */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-primary font-bold text-sm">🏷️ {t("bookingsPage.statusLabel")}</span>
            </div>
            <div
              dir={isEn ? "ltr" : "rtl"}
              data-testid="status-chips"
              className="flex flex-row items-center flex-wrap gap-2"
            >
              {STATUS_CHIP_KEYS.map((key) => {
                const active = statusChip === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`chip-status-${key}`}
                    onClick={() => setStatusChip(key)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-bold"
                        : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                    }`}
                  >
                    <span>{t(`bookingsPage.statusChips.${key}`)}</span>
                    <span
                      className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                      }`}
                    >
                      {fmtNum(statusCounts[key] || 0)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results summary bar */}
          <div className="flex items-center justify-between mb-3 pt-2 border-t border-primary/10">
            <span className="text-white/50 text-xs">
              {t("bookingsPage.showingCount", { count: fmtNum(sortedBookings.length) })}
            </span>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block bg-card border border-primary/15 rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-16 text-center text-primary animate-pulse">{t("bookingsPage.loading")}</div>
            ) : sortedBookings.length === 0 ? (
              <div className="p-16 text-center text-white/50">{t("bookingsPage.empty")}</div>
            ) : (
              <div className="overflow-x-auto" data-testid="bookings-table-container">
                <div
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "top right",
                    width: `${100 / zoom}%`,
                  }}
                  className="origin-top-right transition-transform"
                >
                  <table className="w-full min-w-[1400px] text-sm" data-testid="bookings-table">
                  <thead className="bg-[#0B1A3A] text-primary/80 text-xs">
                    <tr>
                      <th className="p-3 text-start">#</th>
                      <SortHeader label={t("bookingsPage.cols.seq")} col="seq_number" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.company")} col="company" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.client")} col="client_name" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.phone")} col="client_phone" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.date")} col="trip_date" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.day")} col="day_of_week" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} nowrap />
                      <SortHeader label={t("bookingsPage.cols.time")} col="trip_time" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.airline")} col="airline" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} nowrap />
                      <SortHeader label={t("bookingsPage.cols.flightNo")} col="flight_number" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} nowrap />
                      <th className="p-3 text-start whitespace-nowrap">{t("bookingsPage.cols.departure")}</th>
                      <SortHeader label={t("bookingsPage.cols.pickup")} col="pickup_location" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.destination")} col="destination" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.car")} col="car_type" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.passengers")} col="passengers" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.price")} col="price" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.wage")} col="driver_wages" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.commission")} col="agent_commission" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} nowrap />
                      <SortHeader label={t("bookingsPage.cols.cashClient")} col="cash_from_client" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} nowrap />
                      {/* 2026-02-28 §PaidColumn: admin needs a paid-per-route
                          column right next to price so partial settlements
                          are visible without leaving this page. */}
                      {isAdmin && <th className="p-3 text-start whitespace-nowrap">المدفوع</th>}
                      {/* 2026-02-28 §RemainingColumn: editable inverse of paid
                          (paid + remaining = route_total). Editing either one
                          updates the other on the backend. */}
                      {isAdmin && <th className="p-3 text-start whitespace-nowrap">المتبقي</th>}
                      {/* 2026-02-28 §PaymentMethod: free chip next to paid so
                          admins can annotate تحويل/كاش/شيك per route. */}
                      {isAdmin && <th className="p-3 text-start whitespace-nowrap">طريقة الدفع</th>}
                      <SortHeader label={t("bookingsPage.cols.net")} col="net" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                      <SortHeader label={t("bookingsPage.cols.status")} col="status" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} nowrap className="min-w-[110px]" />
                      <th className="p-3 text-start">{t("bookingsPage.cols.driver")}</th>
                      {isAdmin && <th className="p-3 text-center whitespace-nowrap">{t("bookingsPage.cols.actions")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let rowIndex = 0;
                      const out = [];
                      for (const b of sortedBookings) {
                      const i = rowIndex++;
                      const iso = b.pickup_time || combineDateTime(b.trip_date, b.trip_time);
                      const urg = urgencyLevel(iso);
                      const s = urgencyStyle(urg);
                      const _cashClient = Number(b.cash_from_client || 0);
                      const _commission = Number(b.agent_commission || 0);
                      const net = (Number(b.price || b.total_price || 0)) - (Number(b.driver_wage || 0)) - _commission;
                      out.push(
                        <tr
                          key={b._route ? `${b.id}::r${b._routeIdx}${b._carIdx !== undefined ? `c${b._carIdx}` : ""}` : b.id}
                          data-testid={
                            b._route
                              ? `row-${b.id}-route-${b._routeIdx}${b._carIdx !== undefined ? `-c${b._carIdx}` : ""}`
                              : `row-${b.id}`
                          }
                          onClick={(e) => {
                            // Ignore clicks on buttons / links inside the row.
                            if (e.target.closest("button, a, [role='button']")) return;
                            openBooking(b._original_id || b.id);
                          }}
                          className={`border-t border-primary/10 transition-all fadeup cursor-pointer ${
                            isCancelled(b)
                              ? "hover:bg-red-950/40"
                              : lastVisitedId === b.id
                              ? "hover:bg-primary/10"
                              : "hover:bg-primary/5"
                          }`}
                          style={{
                            animationDelay: `${Math.min(i, 10) * 20}ms`,
                            // Rejected/cancelled wins over all other backgrounds.
                            ...(isCancelled(b)
                              ? {
                                  background: "rgba(220,38,38,0.12)",
                                  boxShadow: "inset 3px 0 0 #DC2626",
                                }
                              : lastVisitedId === b.id
                              ? {
                                  background: "rgba(201,169,97,0.10)",
                                  boxShadow: "inset 3px 0 0 #C9A961",
                                }
                              : urg
                              ? { background: s.bg }
                              : undefined),
                          }}
                        >
                          <td className="p-3 num">{fmtNum(i + 1)}</td>
                          <td className="p-3 num text-primary/90">
                            {urg && <span className="pulse-dot inline-block w-2 h-2 rounded-full ml-1" style={{ background: s.dot }} />}
                            {isCancelled(b) && (
                              <span
                                data-testid={`row-cancel-ribbon-${b.id}`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-black bg-red-600 text-white ml-1.5 align-middle"
                              >
                                ملغي
                              </span>
                            )}
                            {movementIndexMap[b.id] && (
                              <span
                                data-testid={`row-movement-${b.id}`}
                                title="ترقيم مستقل لحركات هذه الشركة ضمن النطاق الحالي"
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/15 border border-amber-400/40 text-amber-300 ml-1.5 align-middle"
                              >
                                🔁 <span className="num">{fmtNum(movementIndexMap[b.id])}</span>
                              </span>
                            )}
                            {(() => {
                              const gi = groupInfoMap[b.id];
                              const shown = gi?.groupSeq || b.booking_number;
                              return <span dir="ltr">{toEnglishDigits(shown)}</span>;
                            })()}
                            {groupInfoMap[b.id]?.groupSize > 1 && (
                              <span
                                data-testid={`row-group-movement-${b.id}`}
                                title={`ترتيب هذا المسار داخل مجموعة العميل: ${groupInfoMap[b.id].movement} من ${groupInfoMap[b.id].groupSize}`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4AF37] text-black mr-1.5 align-middle whitespace-nowrap"
                              >
                                حركة <span className="num" dir="ltr">{fmtNum(groupInfoMap[b.id].movement)}/{fmtNum(groupInfoMap[b.id].groupSize)}</span>
                              </span>
                            )}
                            {Array.isArray(b.routes) && b.routes.length > 1 && !b._route && (
                              <span
                                data-testid={`row-routes-count-${b.id}`}
                                title={`هذا الحجز يحتوي على ${b.routes.length} مسارات`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4AF37] text-black mr-1.5 align-middle whitespace-nowrap"
                              >
                                <span dir="ltr">+<span className="num">{fmtNum(b.routes.length)}</span></span> مسار
                              </span>
                            )}
                            {b._route && (
                              <span
                                data-testid={`row-route-badge-${b._original_id || b.id}-r${b._routeIdx}${b._carIdx !== undefined ? `-c${b._carIdx}` : ""}`}
                                title={
                                  (b._dateRouteTotal && b._dateRouteTotal > 1)
                                    ? `المسار ${b._dateRouteIdx + 1} من ${b._dateRouteTotal} في هذا التاريخ (إجمالي ${b._routeTotal} مسارات في الحجز)`
                                    : `المسار ${b._routeIdx + 1} من ${b._routeTotal} داخل هذا الحجز`
                                }
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4AF37] text-black mr-1.5 align-middle whitespace-nowrap"
                              >
                                {(b._dateRouteTotal && b._dateRouteTotal > 1) ? (
                                  <>
                                    <span dir="ltr">+<span className="num">{fmtNum(b._dateRouteTotal)}</span></span> مسار
                                  </>
                                ) : (
                                  <>
                                    مسار <span className="num" dir="ltr">{fmtNum(b._routeIdx + 1)}/{fmtNum(b._routeTotal)}</span>
                                  </>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="p-3">{b.company_name || "—"}</td>
                          <td className="p-3">
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.client_name || ""}
                                field="client_name"
                                type="text"
                                onSaved={patchLocalBooking}
                                testId={`inline-client-${b.id}`}
                                placeholder="—"
                              />
                            ) : (b.client_name || "—")}
                          </td>
                          <td className="p-3 num" dir="ltr">
                            <ClientPhonesCell booking={b} testIdBase={`row-phones-${b.id}`} capacityMap={carsCapacity} />
                          </td>
                          <td className="p-3 num">
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.trip_date || (iso ? iso.slice(0, 10) : "")}
                                field="trip_date"
                                type="date"
                                onSaved={patchLocalBooking}
                                testId={`inline-date-${b.id}`}
                                display={(v) => <span className="num">{fmtDate(v)}</span>}
                              />
                            ) : fmtDate(iso)}
                          </td>
                          <td className="p-3 text-primary whitespace-nowrap">{arabicDayOfWeek(b.trip_date || (iso || "").slice(0, 10))}</td>
                          <td className="p-3 num" style={{ color: urg ? s.text : undefined }}>
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.trip_time || (iso ? iso.slice(11, 16) : "")}
                                field="trip_time"
                                type="time"
                                onSaved={patchLocalBooking}
                                testId={`inline-time-${b.id}`}
                              />
                            ) : (<>{fmtTime(iso)}</>)}
                            {urg && <div className="text-[10px]" style={{ color: s.text }}>{timeUntil(iso, fmtNum)}</div>}
                          </td>
                          {(() => {
                            const al = getBookingAirline(b);
                            const trainMode =
                              al === "قطار" ||
                              isTrainStation(b.pickup_location) ||
                              isTrainStation(b.destination);
                            if (trainMode) {
                              return (
                                <td className="p-3 whitespace-nowrap text-xs">
                                  <span
                                    data-testid={`train-badge-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                    title="رحلة عبر القطار — حقول الطيران معطّلة"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold bg-[#4A90E2] text-white"
                                  >
                                    🚄 قطار
                                  </span>
                                </td>
                              );
                            }
                            return (
                              <td className="p-3 whitespace-nowrap text-xs font-semibold" style={{ color: al ? "#C9A961" : "rgba(255,255,255,0.35)" }}>
                                {al || "—"}
                              </td>
                            );
                          })()}
                          <td className="p-3 num" dir="ltr">
                            {(() => {
                              const trainMode =
                                getBookingAirline(b) === "قطار" ||
                                isTrainStation(b.pickup_location) ||
                                isTrainStation(b.destination);
                              if (trainMode) {
                                return <span className="text-white/30">—</span>;
                              }
                              return isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={getBookingFlightNumber(b)}
                                field="flight_number"
                                type="text"
                                onSaved={patchLocalBooking}
                                testId={`inline-flight-${b.id}`}
                                placeholder="—"
                                display={(v) => (
                                  <span
                                    className="num font-semibold"
                                    style={{ color: "#7DD3FC" /* sky-300 — info blue */ }}
                                    dir="ltr"
                                  >
                                    {v}
                                  </span>
                                )}
                              />
                            ) : (() => {
                                const fn = getBookingFlightNumber(b);
                                return fn
                                  ? <span className="num font-semibold" style={{ color: "#7DD3FC" }} dir="ltr">{fn}</span>
                                  : "—";
                              })();
                            })()}
                          </td>
                          {(() => {
                            const dep = computeDepartureRule(
                              b.pickup_location,
                              b.destination || b.dropoff_location,
                              b.trip_time,
                              b.departure_time_driver || b.departure_time_client,
                            );
                            return dep ? (
                              <td className="p-3 num font-semibold whitespace-nowrap" style={{ color: "#3B82F6" }} dir="ltr" title={dep.label}>{dep.time}</td>
                            ) : (
                              <td className="p-3 num text-white/40" dir="ltr">—</td>
                            );
                          })()}
                          <td className="p-3">
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.pickup_location || ""}
                                displayValue={translateLocation(b.pickup_location, i18n.language)}
                                field="pickup_location"
                                type="text"
                                onSaved={patchLocalBooking}
                                testId={`inline-pickup-${b.id}`}
                                inputProps={{ style: { minWidth: 160 } }}
                              />
                            ) : translateLocation(b.pickup_location, i18n.language)}
                          </td>
                          <td className="p-3">
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.destination || b.dropoff_location || ""}
                                displayValue={translateLocation(b.destination || b.dropoff_location, i18n.language)}
                                field="destination"
                                type="text"
                                onSaved={patchLocalBooking}
                                testId={`inline-dest-${b.id}`}
                                inputProps={{ style: { minWidth: 160 } }}
                              />
                            ) : translateLocation(b.destination || b.dropoff_location, i18n.language)}
                          </td>
                          <td className="p-3">
                            <div className="inline-flex items-center gap-1.5">
                              <span>{b.car_type_name || b.car_type || "—"}</span>
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Trigger upgrade modal from parent page.
                                    // We pass the row's route_idx + car_idx
                                    // so the backend upgrades the correct
                                    // entry inside routes[N].cars[K].
                                    setUpgradeFor({
                                      ...b,
                                      _upgradeRouteIdx: b._route ? b._routeIdx : null,
                                      _upgradeCarIdx: b._route && b._carIdx !== undefined ? b._carIdx : null,
                                    });
                                  }}
                                  data-testid={`btn-upgrade-car-${b.id}${b._route ? `-r${b._routeIdx}${b._carIdx !== undefined ? `-c${b._carIdx}` : ""}` : ""}`}
                                  title="ترقية السيارة"
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold text-black hover:opacity-80"
                                  style={{ backgroundColor: "#00E1FF" }}
                                >
                                  <ArrowUp size={11} weight="bold" />
                                </button>
                              )}
                            </div>
                          </td>
                          {(() => {
                            // Show ONLY what the company entered (booking.passengers).
                            // capacity × quantity is a PDF-only calculation (2026-02 spec).
                            const stored = Number(b.passengers || 0);
                            const display = stored;
                            if (isAdmin) {
                              return (
                                <td
                                  className="p-3 num font-semibold"
                                  title="عدد الركاب من قيمة الشركة — انقر للتعديل اليدوي"
                                >
                                  {b._route && b._routeIdx > 0 ? (
                                    <span className="num font-semibold text-white/60">
                                      {display ? fmtNum(display) : "—"}
                                    </span>
                                  ) : (
                                    <InlineCell
                                      bookingId={realBookingId(b.id)}
                                      value={stored || 1}
                                      field="passengers"
                                      type="number"
                                      cancelOnBlur
                                      onSaved={patchLocalBooking}
                                      testId={`inline-passengers-${b.id}`}
                                      inputProps={{ min: 1, step: 1, style: { width: 60 } }}
                                      display={() => (
                                        <span className="num font-semibold">
                                          {display ? fmtNum(display) : "—"}
                                        </span>
                                      )}
                                    />
                                  )}
                                </td>
                              );
                            }
                            return (
                              <td className="p-3 num font-semibold">
                                {display ? fmtNum(display) : "—"}
                              </td>
                            );
                          })()}
                          <td className="p-3 num text-primary">
                            {isAdmin && !b._route ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.price || b.total_price || 0}
                                field="total_price"
                                type="number"
                                onSaved={patchLocalBooking}
                                testId={`inline-price-${b.id}`}
                                inputProps={{ min: 0, step: 0.01 }}
                                display={(v) => <span className="num text-primary">{fmtNum(v)}</span>}
                              />
                            ) : (
                              // Multi-route expanded row → read-only per-leg price.
                              // Editing `total_price` here would overwrite the WHOLE
                              // booking's aggregate — which is misleading in a per-
                              // route row. Spec 2026-07-23 v3.4.
                              <span
                                className="num text-primary"
                                title={b._route ? `سعر هذا المسار فقط — لتعديل، افتح تفاصيل الحجز` : undefined}
                                data-testid={`row-price-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                              >
                                {fmtNum(b.price || b.total_price)}
                              </span>
                            )}
                          </td>
                          <td className="p-3 num text-[#F57F17]">
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={b.driver_wage || 0}
                                type="number"
                                endpoint={`/admin/bookings/${realBookingId(b.id)}/total-wage`}
                                method="put"
                                buildPayload={(v) => {
                                  const payload = { driver_wage: v };
                                  if (b._route) {
                                    payload.route_idx = b._routeIdx;
                                    if (b._carIdx !== undefined) payload.car_idx = b._carIdx;
                                  }
                                  return payload;
                                }}
                                onSaved={(u) => patchLocalBooking(u.id === realBookingId(b.id) ? { ...u, id: u.id } : u)}
                                testId={`inline-wage-${b.id}${b._route ? `-r${b._routeIdx}${b._carIdx !== undefined ? `-c${b._carIdx}` : ""}` : ""}`}
                                inputProps={{ min: 0, step: 0.01 }}
                                display={(v) => <span className="num text-[#F57F17]">{fmtNum(v)}</span>}
                              />
                            ) : fmtNum(b.driver_wage)}
                          </td>
                          <td
                            className="p-3 num font-bold"
                            data-testid={`row-commission-${b.id}`}
                          >
                            {/* 2026-02-28 §InlineFinancials: admin can now
                                edit agent_commission directly here without
                                opening the booking detail page. Writes to
                                routes[N].agent_commission when this row is
                                an exploded per-route row (b._route present);
                                otherwise writes to the booking-level field. */}
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={_commission || 0}
                                type="number"
                                endpoint={`/admin/bookings/${realBookingId(b.id)}/route-financials`}
                                method="patch"
                                buildPayload={(v) => {
                                  const payload = { agent_commission: Number(v) || 0 };
                                  if (b._route) payload.route_idx = b._routeIdx;
                                  return payload;
                                }}
                                onSaved={(u) => patchLocalBooking(u.id === realBookingId(b.id) ? { ...u, id: u.id } : u)}
                                testId={`inline-commission-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                inputProps={{ min: 0, step: 0.01 }}
                                display={(v) => Number(v) > 0
                                  ? <span className="num" style={{ color: "#B45309" }}>{fmtNum(v)}</span>
                                  : <span className="text-white/35">—</span>}
                              />
                            ) : (_commission > 0
                              ? <span className="num" style={{ color: "#B45309" }}>{fmtNum(_commission)}</span>
                              : <span className="text-white/35">—</span>)}
                          </td>
                          <td
                            className="p-3 num font-bold"
                            data-testid={`row-cash-${b.id}`}
                          >
                            {/* 2026-02-28 §InlineFinancials: same as commission,
                                inline-editable cash_from_client per route. */}
                            {isAdmin ? (
                              <InlineCell
                                bookingId={realBookingId(b.id)}
                                value={_cashClient || 0}
                                type="number"
                                endpoint={`/admin/bookings/${realBookingId(b.id)}/route-financials`}
                                method="patch"
                                buildPayload={(v) => {
                                  const payload = { cash_from_client: Number(v) || 0 };
                                  if (b._route) payload.route_idx = b._routeIdx;
                                  return payload;
                                }}
                                onSaved={(u) => patchLocalBooking(u.id === realBookingId(b.id) ? { ...u, id: u.id } : u)}
                                testId={`inline-cash-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                inputProps={{ min: 0, step: 0.01 }}
                                display={(v) => Number(v) > 0
                                  ? <span className="num" style={{ color: "#3B82F6" }}>{fmtNum(v)}</span>
                                  : <span className="text-white/35">—</span>}
                              />
                            ) : (_cashClient > 0
                              ? <span className="num" style={{ color: "#3B82F6" }}>{fmtNum(_cashClient)}</span>
                              : <span className="text-white/35">—</span>)}
                          </td>
                          {/* 2026-02-28 §PaidColumn: per-route paid cell.
                              For exploded rows shows this route's own
                              paid_amount only (backend keeps booking-level
                              paid = sum of routes[*].paid_amount).
                              Non-primary cars of the same route show —
                              (paid belongs to the route, not the car). */}
                          {isAdmin && (
                            <td className="p-3 num font-bold" data-testid={`row-paid-${b.id}`}>
                              {b._route && b._carIdx > 0 ? (
                                <span className="text-white/25 text-[10px]">—</span>
                              ) : (
                                <InlineCell
                                  bookingId={realBookingId(b.id)}
                                  value={Number(b.paid_amount || 0)}
                                  type="number"
                                  endpoint={`/admin/bookings/${realBookingId(b.id)}/route-financials`}
                                  method="patch"
                                  buildPayload={(v) => {
                                    const payload = { paid_amount: Number(v) || 0 };
                                    if (b._route) payload.route_idx = b._routeIdx;
                                    return payload;
                                  }}
                                  onSaved={(u) => patchLocalBooking(u?.booking || u)}
                                  testId={`inline-paid-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  display={(v) => Number(v) > 0
                                    ? <span className="num text-[#2E7D32]">{fmtNum(v)}</span>
                                    : <span className="text-white/35">—</span>}
                                />
                              )}
                            </td>
                          )}
                          {/* 2026-02-28 §RemainingColumn: editable inverse.
                              remaining = route_total - paid. Editing this
                              cell sets `paid = route_total - remaining` on
                              the backend so admin can enter the amount
                              STILL OWED and the system infers what's paid. */}
                          {isAdmin && (() => {
                            const rowTotal = Number(b.price || b.total_price || 0);
                            const rowPaid = Number(b.paid_amount || 0);
                            const remaining = Math.max(0, rowTotal - rowPaid);
                            return (
                              <td className="p-3 num font-bold" data-testid={`row-remaining-${b.id}`}>
                                {b._route && b._carIdx > 0 ? (
                                  <span className="text-white/25 text-[10px]">—</span>
                                ) : (
                                  <InlineCell
                                    bookingId={realBookingId(b.id)}
                                    value={remaining}
                                    type="number"
                                    endpoint={`/admin/bookings/${realBookingId(b.id)}/route-financials`}
                                    method="patch"
                                    buildPayload={(v) => {
                                      const payload = { remaining_amount: Number(v) || 0 };
                                      if (b._route) payload.route_idx = b._routeIdx;
                                      return payload;
                                    }}
                                    onSaved={(u) => patchLocalBooking(u?.booking || u)}
                                    testId={`inline-remaining-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    display={(v) => Number(v) > 0
                                      ? <span className="num text-[#F57F17]">{fmtNum(v)}</span>
                                      : <span className="text-white/35">مدفوع بالكامل</span>}
                                  />
                                )}
                              </td>
                            );
                          })()}
                          {/* 2026-02-28 §PaymentMethod: dropdown of preset
                              chips (تحويل / كاش / شيك) + custom option. Sent
                              as `payment_method` string. */}
                          {isAdmin && (
                            <td className="p-3 whitespace-nowrap" data-testid={`row-payment-method-${b.id}`}>
                              {b._route && b._carIdx > 0 ? (
                                <span className="text-white/25 text-[10px]">—</span>
                              ) : (
                                <InlineCell
                                  bookingId={realBookingId(b.id)}
                                  value={b.payment_method || ""}
                                  type="select"
                                  options={[
                                    { value: "", label: "—" },
                                    { value: "تحويل", label: "تحويل" },
                                    { value: "كاش", label: "كاش" },
                                    { value: "مختلط", label: "مختلط" },
                                  ]}
                                  endpoint={`/admin/bookings/${realBookingId(b.id)}/route-financials`}
                                  method="patch"
                                  buildPayload={(v) => {
                                    const payload = { payment_method: String(v || "") };
                                    if (b._route) payload.route_idx = b._routeIdx;
                                    return payload;
                                  }}
                                  onSaved={(u) => patchLocalBooking(u?.booking || u)}
                                  testId={`inline-payment-method-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                  display={(v) => {
                                    const s = String(v || "").trim();
                                    if (!s) return <span className="text-white/35">—</span>;
                                    const styles = {
                                      "تحويل": { bg: "#3B82F6", border: "#3B82F6" },
                                      "كاش":   { bg: "#22C55E", border: "#22C55E" },
                                      "مختلط": { bg: "#8B5CF6", border: "#8B5CF6" },
                                    };
                                    const st = styles[s] || { bg: "#94A3B8", border: "#94A3B8" };
                                    return (
                                      <span
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                                        style={{
                                          background: `${st.bg}22`,
                                          border: `1px solid ${st.border}66`,
                                          color: st.bg,
                                        }}
                                      >
                                        {s}
                                      </span>
                                    );
                                  }}
                                />
                              )}
                            </td>
                          )}
                          <td className={`p-3 num font-bold ${net >= 0 ? "text-[#2E7D32]" : "text-destructive"}`}>{fmtNum(net)}</td>
                          <td className="p-3 whitespace-nowrap">
                            <span className={`inline-block whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs border ${statusStyle(b.status)}`}
                              data-testid={`row-status-${b.id}`}
                            >
                              {t(statusLabelKey(b.status))}
                            </span>
                          </td>
                          <td className="p-3">
                            {isAdmin ? (
                              <div className="flex flex-col gap-1">
                                {(() => {
                                  // 2026-02-28 §DriverDisplayResolved: since
                                  // flatAll materialises the per-car → per-route
                                  // → booking-root driver fallback onto the row's
                                  // root (`...rowDriver`), we can safely read
                                  // `b.driver_*` here. This unifies the three
                                  // branches (multi-route/single-route/legacy)
                                  // and prevents driver mismatch between the
                                  // display cell and the wage/rowDriver values
                                  // materialised by flatAll.
                                  const rDriver = {
                                    driver_id: b.driver_id ?? null,
                                    driver_name: b.driver_name ?? null,
                                    driver_phone: b.driver_phone ?? null,
                                    plate_number: b.plate_number ?? null,
                                    external_driver_name: b.external_driver_name ?? null,
                                    external_driver_phone: b.external_driver_phone ?? null,
                                  };
                                  // Test-id suffix uniquely identifies the row.
                                  const suffix = b._route ? `-r${b._routeIdx}${b._carIdx !== undefined ? `-c${b._carIdx}` : ""}` : "";
                                  // 2026-02-26 §MultiDayExplode: split rows
                                  // carry composite id `{orig}__{YYYY-MM-DD}` —
                                  // the DriverAssignModal (and its downstream
                                  // /admin/bookings/{id}/assign endpoint) MUST
                                  // receive the REAL booking id, else the API
                                  // returns 404. Strip via `realBookingId`.
                                  const openAssign = () => setAssignFor({
                                    ...b,
                                    id: realBookingId(b.id),
                                    _original_id: realBookingId(b.id),
                                    _assignRouteIdx: b._route ? b._routeIdx : null,
                                    _assignCarIdx: (b._route && b._carIdx !== undefined) ? b._carIdx : null,
                                  });
                                  return (rDriver.driver_name || rDriver.external_driver_name) ? (
                                  <>
                                    <div className="text-white text-xs">
                                      {rDriver.driver_name || rDriver.external_driver_name}
                                      {rDriver.external_driver_name && !rDriver.driver_name && (
                                        <span className="mr-1 text-[10px] text-[#FBBF24]">(خارجي)</span>
                                      )}
                                      {b.allow_double_booking && (
                                        <span
                                          data-testid={`double-badge-${b.id}`}
                                          className="mr-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#F57F17]/20 border border-[#F57F17]/60 text-[#F57F17] font-bold"
                                          title="السائق أُسند لهذا الحجز رغم وجود حجز آخر متزامن"
                                        >
                                          ⚠️ مضاعف
                                        </span>
                                      )}
                                    </div>
                                    {rDriver.plate_number && (
                                      <div className="text-[10px] text-white/40 num" dir="ltr">
                                        {rDriver.plate_number}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {(() => {
                                        const drv = rDriver.driver_id
                                          ? drivers.find((d) => d.id === rDriver.driver_id)
                                          : { name: rDriver.external_driver_name, phone: rDriver.external_driver_phone };
                                        const url = buildDriverWhatsAppUrl({ ...b, ...rDriver }, drv, { capacityMap: carsCapacity });
                                        return url ? (
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`btn-wa-driver-${b.id}${suffix}`}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/15"
                                          >
                                            <WhatsappLogo size={10} weight="fill" /> واتساب
                                          </a>
                                        ) : null;
                                      })()}
                                      <button
                                        type="button"
                                        onClick={openAssign}
                                        data-testid={`btn-change-driver-${b.id}${suffix}`}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-primary/40 text-primary/90 hover:bg-primary/10"
                                      >
                                        <PencilSimple size={10} /> تغيير
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={openAssign}
                                    data-testid={`btn-assign-driver-${b.id}${suffix}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/25"
                                  >
                                    <UserPlus size={12} weight="bold" /> تعيين سائق
                                  </button>
                                );
                                })()}
                              </div>
                            ) : (
                              b.driver_name || b.external_driver_name || "—"
                            )}
                          </td>
                          {isAdmin && (
                            <td className="p-3 text-center whitespace-nowrap">
                              {b._route && b._routeIdx > 0 ? (
                                <span className="text-white/25 text-[10px]">—</span>
                              ) : (
                                <div className="inline-flex items-center gap-1.5">
                                  {/* 2026-02-25 §QuickStatus: for pending
                                      bookings, admin can confirm/reject
                                      directly from the table without opening
                                      the details page. Only on the FIRST row
                                      of a booking (primary row). */}
                                  {b.status === "pending" && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const { data } = await api.post(`/bookings/${b._original_id || b.id}/accept`);
                                            patchLocalBooking(data);
                                            toast.success("تم تأكيد الحجز");
                                          } catch (err) {
                                            toast.error(err?.response?.data?.detail || "فشل تأكيد الحجز");
                                          }
                                        }}
                                        data-testid={`btn-confirm-booking-${b.id}`}
                                        title="تأكيد الحجز"
                                        aria-label="تأكيد الحجز"
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#22C55E] text-white hover:bg-[#16A34A] transition-colors shadow-sm"
                                      >
                                        <CheckCircle size={15} weight="bold" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!window.confirm(`رفض الحجز ${b.seq_number || b.booking_number || ""}؟`)) return;
                                          try {
                                            const { data } = await api.patch(`/admin/bookings/${b._original_id || b.id}`, { status: "rejected" });
                                            patchLocalBooking(data);
                                            toast.success("تم رفض الحجز");
                                          } catch (err) {
                                            toast.error(err?.response?.data?.detail || "فشل رفض الحجز");
                                          }
                                        }}
                                        data-testid={`btn-reject-booking-${b.id}`}
                                        title="رفض الحجز"
                                        aria-label="رفض الحجز"
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F57F17] text-white hover:bg-[#E65100] transition-colors shadow-sm"
                                      >
                                        <XCircle size={15} weight="bold" />
                                      </button>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openDeleteFlow(b); }}
                                    data-testid={`btn-delete-booking-${b.id}`}
                                    title="حذف الحجز نهائياً"
                                    aria-label="حذف الحجز نهائياً"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#C62828] text-white hover:bg-[#B71C1C] transition-colors shadow-sm"
                                  >
                                    <Trash size={15} weight="bold" />
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>,
                      );
                      }
                      return out;
                    })()}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {loading ? (
              <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
            ) : bookingsForMobile.length === 0 ? (
              <div className="text-center py-16 text-white/50">لا توجد حجوزات مطابقة</div>
            ) : (
              bookingsForMobile.map((b, i) => {
                const iso = b.pickup_time || combineDateTime(b.trip_date, b.trip_time);
                const urg = urgencyLevel(iso);
                const s = urgencyStyle(urg);
                const driver = drivers.find((d) => d.id === b.driver_id);
                const isRejected = isCancelled(b);
                const isLastVisited = lastVisitedId === b.id && !isRejected;
                const movIdx = movementIndexMap[b.id];
                // Rejected background wins over urgency and last-visited.
                const cardStyle = {
                  animationDelay: `${Math.min(i, 10) * 30}ms`,
                  ...(isRejected
                    ? { background: "rgba(220,38,38,0.12)" }
                    : isLastVisited
                    ? { background: "rgba(201,169,97,0.14)" }
                    : urg
                    ? { background: `linear-gradient(90deg, ${s.bg}, transparent 60%), #0F1F42`, borderRightWidth: "4px", borderRightColor: s.border }
                    : undefined),
                };
                const cardClass = isRejected
                  ? "bg-card border-2 border-[#DC2626] rounded-2xl p-4 fadeup relative"
                  : isLastVisited
                  ? "bg-card border-2 border-primary rounded-2xl p-4 fadeup relative"
                  : "bg-card border border-primary/15 rounded-2xl p-4 fadeup relative cursor-pointer hover:border-primary/40 transition-colors";
                return (
                  <div
                    key={b._route ? `${b.id}::r${b._routeIdx}${b._carIdx !== undefined ? `c${b._carIdx}` : ""}` : b.id}
                    data-testid={
                      b._route
                        ? `card-${b.id}-route-${b._routeIdx}${b._carIdx !== undefined ? `-c${b._carIdx}` : ""}`
                        : `card-${b.id}`
                    }
                    onClick={(e) => {
                      if (e.target.closest("button, a, [role='button']")) return;
                      openBooking(b._original_id || b.id);
                    }}
                    className={cardClass}
                    style={cardStyle}
                  >
                    {isRejected && (
                      <span
                        data-testid={`card-cancel-ribbon-${b.id}`}
                        className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-black bg-red-600 text-white shadow"
                      >
                        ملغي
                      </span>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-primary text-sm num flex items-center gap-1 flex-wrap" dir="ltr">
                          {urg && <span className="pulse-dot inline-block w-2 h-2 rounded-full" style={{ background: s.dot }} />}
                          {toEnglishDigits(groupInfoMap[b.id]?.groupSeq || b.booking_number)}
                          {groupInfoMap[b.id]?.groupSize > 1 && (
                            <span
                              data-testid={`card-group-movement-${b.id}`}
                              title={`ترتيب هذا المسار داخل مجموعة العميل: ${groupInfoMap[b.id].movement} من ${groupInfoMap[b.id].groupSize}`}
                              dir="rtl"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4AF37] text-black mr-1 whitespace-nowrap"
                            >
                              حركة <span className="num" dir="ltr">{fmtNum(groupInfoMap[b.id].movement)}/{fmtNum(groupInfoMap[b.id].groupSize)}</span>
                            </span>
                          )}
                          {Array.isArray(b.routes) && b.routes.length > 1 && !b._route && (
                            <span
                              data-testid={`card-routes-count-${b.id}`}
                              title={`هذا الحجز يحتوي على ${b.routes.length} مسارات`}
                              dir="rtl"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4AF37] text-black mr-1 whitespace-nowrap"
                            >
                              <span dir="ltr">+<span className="num">{fmtNum(b.routes.length)}</span></span> مسار
                            </span>
                          )}
                          {b._route && (
                            <span
                              data-testid={`card-route-badge-${b.id}-${b._routeIdx}`}
                              title={`المسار ${b._routeIdx + 1} من ${b._routeTotal} داخل هذا الحجز`}
                              dir="rtl"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#D4AF37] text-black mr-1 whitespace-nowrap"
                            >
                              مسار <span className="num" dir="ltr">{fmtNum(b._routeIdx + 1)}/{fmtNum(b._routeTotal)}</span>
                            </span>
                          )}
                          {movIdx && (
                            <span
                              data-testid={`card-movement-${b.id}`}
                              title="ترقيم مستقل لحركات هذه الشركة ضمن النطاق الحالي"
                              dir="rtl"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/15 border border-amber-400/40 text-amber-300 mr-1"
                            >
                              🔁 حركة <span className="num">{fmtNum(movIdx)}</span>
                            </span>
                          )}
                        </div>
                        <div className="text-white font-bold mt-1">{b.client_name || "—"}</div>
                        {(b.client_phones?.length > 0 || b.client_phone) && (
                          <div className="mt-2">
                            <ClientPhonesCell booking={b} testIdBase={`card-phones-${b.id}`} capacityMap={carsCapacity} />
                          </div>
                        )}
                      </div>
                      <span className={`whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs border ${statusStyle(b.status)}`}>
                        {t(statusLabelKey(b.status))}
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-white/80 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="num" style={{ color: urg ? s.text : "#C9A961" }}>{fmtDate(iso)} · {fmtTime(iso)}</span>
                        <span className="text-primary/80 text-xs">· {arabicDayOfWeek(b.trip_date || (iso || "").slice(0, 10))}</span>
                        {urg && <span className="text-xs" style={{ color: s.text }}>· {timeUntil(iso, fmtNum)}</span>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <span>{b.pickup_location}</span>
                        <ArrowLeft size={12} className="text-white/40" />
                        <span>{b.destination || b.dropoff_location}</span>
                      </div>
                      <div className="text-xs text-white/60">
                        {b.car_type_name || b.car_type} · <span className="num">{fmtNum(b.passengers)}</span> ركاب
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-primary/10 flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-primary font-bold num" data-testid={`card-price-${b.id}`}>{fmtRiyal(b.price || b.total_price)}</div>
                        {/* 2026-02-28 §MobileCardPaid — mirror the desktop
                            table cells (paid, remaining, payment method)
                            so admins can settle from mobile without
                            opening the detail page. Inline-editable. */}
                        {isAdmin && !(b._route && b._carIdx > 0) && (() => {
                          const rowTotal = Number(b.price || b.total_price || 0);
                          const rowPaid = Number(b.paid_amount || 0);
                          const remaining = Math.max(0, rowTotal - rowPaid);
                          const bookingId = realBookingId(b.id);
                          const routePayload = (extra) => {
                            const p = { ...extra };
                            if (b._route) p.route_idx = b._routeIdx;
                            return p;
                          };
                          return (
                            <div className="flex items-center gap-2 flex-wrap text-[10px]">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2E7D32]/15 border border-[#2E7D32]/40">
                                <span className="text-[#2E7D32]/80">مدفوع</span>
                                <InlineCell
                                  bookingId={bookingId}
                                  value={rowPaid}
                                  type="number"
                                  endpoint={`/admin/bookings/${bookingId}/route-financials`}
                                  method="patch"
                                  buildPayload={(v) => routePayload({ paid_amount: Number(v) || 0 })}
                                  onSaved={(u) => patchLocalBooking(u?.booking || u)}
                                  testId={`card-paid-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                  display={(v) => Number(v) > 0
                                    ? <span className="num text-[#2E7D32] font-bold">{fmtNum(v)}</span>
                                    : <span className="text-white/40 num">0</span>}
                                />
                              </span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F57F17]/15 border border-[#F57F17]/40">
                                <span className="text-[#F57F17]/80">متبقي</span>
                                <InlineCell
                                  bookingId={bookingId}
                                  value={remaining}
                                  type="number"
                                  endpoint={`/admin/bookings/${bookingId}/route-financials`}
                                  method="patch"
                                  buildPayload={(v) => routePayload({ remaining_amount: Number(v) || 0 })}
                                  onSaved={(u) => patchLocalBooking(u?.booking || u)}
                                  testId={`card-remaining-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                  display={(v) => Number(v) > 0
                                    ? <span className="num text-[#F57F17] font-bold">{fmtNum(v)}</span>
                                    : <span className="text-white/40 num">0</span>}
                                />
                              </span>
                              <InlineCell
                                bookingId={bookingId}
                                value={b.payment_method || ""}
                                type="select"
                                options={[
                                  { value: "", label: "طريقة الدفع" },
                                  { value: "تحويل", label: "تحويل" },
                                  { value: "كاش", label: "كاش" },
                                  { value: "مختلط", label: "مختلط" },
                                ]}
                                endpoint={`/admin/bookings/${bookingId}/route-financials`}
                                method="patch"
                                buildPayload={(v) => routePayload({ payment_method: String(v || "") })}
                                onSaved={(u) => patchLocalBooking(u?.booking || u)}
                                testId={`card-payment-method-${b.id}${b._route ? `-r${b._routeIdx}` : ""}`}
                                display={(v) => {
                                  const s = String(v || "").trim();
                                  if (!s) return <span className="text-white/40">طريقة الدفع</span>;
                                  return <span className="num text-primary font-bold">{s}</span>;
                                }}
                              />
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => setAssignFor({ ...b, id: realBookingId(b.id), _original_id: realBookingId(b.id) })}
                            data-testid={`btn-assign-card-${b.id}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                              b.driver_name || b.external_driver_name
                                ? "bg-primary/10 border-primary/40 text-primary/90"
                                : "bg-[#22C55E]/15 border-[#22C55E]/50 text-[#22C55E]"
                            }`}
                          >
                            {b.driver_name || b.external_driver_name ? (
                              <><PencilSimple size={12} /> تغيير السائق</>
                            ) : (
                              <><UserPlus size={12} weight="bold" /> تعيين سائق</>
                            )}
                          </button>
                        )}
                        {(driver?.phone || b.client_phone) && (
                          <a
                            href={buildWhatsAppLink(b, driver?.phone)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2E7D32]/15 border border-[#2E7D32]/50 text-[#2E7D32] text-xs"
                          >
                            <WhatsappLogo size={14} weight="fill" /> واتساب
                          </a>
                        )}
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openDeleteFlow(b); }}
                            data-testid={`card-delete-booking-${b.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C62828] text-white hover:bg-[#B71C1C] text-xs"
                          >
                            <Trash size={13} weight="bold" /> حذف
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>

      {assignFor && (
        <DriverAssignModal
          booking={assignFor}
          groupSiblings={
            assignFor.booking_group_id
              ? bookings.filter((x) => x.booking_group_id === assignFor.booking_group_id)
              : []
          }
          onClose={() => setAssignFor(null)}
          onSaved={(updated) => {
            setAssignFor(null);
            if (updated && updated.id) {
              // Optimistic patch — avoids the "wage disappears and comes back"
              // flicker caused by the full reload gap (2026-02 fix).
              patchLocalBooking(updated);
            } else {
              load();
            }
          }}
        />
      )}

      {upgradeFor && (
        <UpgradeCarModal
          booking={upgradeFor}
          onClose={() => setUpgradeFor(null)}
          onSaved={() => { setUpgradeFor(null); load(); }}
        />
      )}

      {/* 2026-02-24 §DeleteSimplify: two-step "type حذف" flow removed —
          `openDeleteFlow` now uses a single native window.confirm. */}
    </div>
  );
}
